/**
 * The Glass — the stewardship desk (wave glass-7, #807; THE GLASS ruled
 * #786; design-786 §4 Phase 7, the knowledge golden path).
 *
 * A sub-route of The Glass: stamps and routes awaiting ratification,
 * rendered as Chris's queue, from canon — activeness.yaml and entrance.yaml
 * packaged verbatim and whole at build time, plus the generator's DECLARED
 * ratification-phrase derivation (phrase presence only; see
 * generate-glass-canon.mjs). This pane reads NO live system at render time.
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — queue membership is the MEASURED phrase rule: the entry's
 * ratification mark carries a pending phrase (pendingPhrase non-null, with
 * or without a "RATIFIED" beside it), plus the entrance while its own
 * status field reads draft; one artifact renders one card; every label
 * quotes the files' own text; freshness is the dates the files carry, never
 * auto-judged. The covered-vs-findable section is a join of the packaged
 * routes against the already-packaged register rows (glass-3) — measured
 * hits and misses, never a verdict. The new-document path section is a
 * pointer to the tool, not a claim about it.
 */

import { useMemo } from "react";
import {
  GLASS_CANON,
  type GlassActivenessSurface,
  type GlassRatificationMark,
  type GlassRegisterRow,
} from "@command-center/shared";
import {
  MARK_GROUP_MEASURE,
  MARK_GROUP_ORDER,
  PENDING_PHRASE_PATTERN,
  groupSurfaces,
  routeRegisterLines,
  type MarkedSurface,
} from "./stewardship-derive";

const NEW_DOCUMENT_TOOL = "engineering/contract/bin/new-document.py";

export function GlassStewardshipPage() {
  const { activeness, entrance } = GLASS_CANON.stewardship;

  const groups = useMemo(
    () =>
      groupSurfaces(activeness.data.surfaces, activeness.ratificationMarks),
    [activeness],
  );
  const rowsByPath = useMemo(() => {
    const m = new Map<string, GlassRegisterRow>();
    for (const r of GLASS_CANON.register.rows) m.set(r.path, r);
    return m;
  }, []);
  const routeLines = useMemo(
    () => routeRegisterLines(activeness.data.surfaces, rowsByPath),
    [activeness, rowsByPath],
  );

  const entranceInQueue = entrance.data.status === "draft";
  /** The queue, one card per artifact: every surface whose ratification mark
   *  carries a pending phrase (pendingPhrase non-null — the pending-phrase-only
   *  and both groups), plus the surface whose ref equals entrance.yaml's
   *  packaged sourcePath while that file's status field reads draft, if the
   *  first rule did not already put it here. */
  const queue = useMemo(() => {
    const marked = [
      ...(groups.get("pending-phrase-only") ?? []),
      ...(groups.get("both") ?? []),
    ];
    if (!entranceInQueue) return marked;
    if (marked.some((m) => m.surface.ref === entrance.sourcePath)) return marked;
    const byRef = [
      ...(groups.get("ratified-only") ?? []),
      ...(groups.get("neither") ?? []),
    ].filter((m) => m.surface.ref === entrance.sourcePath);
    return [...marked, ...byRef];
  }, [groups, entranceInQueue, entrance.sourcePath]);

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Stewardship</h1>
      <p className="mb-1 text-xs text-plane-muted">
        renders <code>{GLASS_CANON.meta.sources.activeness}</code> (
        {activeness.data.surfaces.length} surface entries, maintained by{" "}
        {activeness.data.maintained_by}, seeded {activeness.data.seeded}) and{" "}
        <code>{GLASS_CANON.meta.sources.entrance}</code> (status:{" "}
        {entrance.data.status}), packaged at build time; ratification marks
        are the generator's declared phrase-presence derivation — this page
        regroups and quotes, read-only.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        register lines join route refs against the packaged{" "}
        <code>{GLASS_CANON.meta.sources.register}</code> rows by exact path.
      </p>

      <QueueSection
        queue={queue}
        entranceStatus={entrance.data.status}
        entranceSource={entrance.sourcePath}
        entranceInQueue={entranceInQueue}
      />
      <AllSurfacesSection groups={groups} />
      <CoveredFindableSection lines={routeLines} />
      <NewDocumentSection />
    </section>
  );
}

/** The queue — one card per artifact, membership by the measured rule: the
 *  entry's ratification mark carries a pending phrase (pendingPhrase
 *  non-null, RATIFIED present or not), plus the entrance while its own
 *  status field reads draft. Labels quote the files; nothing here says
 *  "needs". */
function QueueSection({
  queue,
  entranceStatus,
  entranceSource,
  entranceInQueue,
}: {
  queue: readonly MarkedSurface[];
  entranceStatus: string;
  entranceSource: string;
  entranceInQueue: boolean;
}) {
  return (
    <>
      <SectionHeading
        label="The queue"
        title={`Awaiting ratification — ${queue.length} entries by the measured rule`}
        note={`membership measured, never judged: the entry's ratification mark carries a pending phrase — its warrant/presence_exempt text matches the generator-declared ratify-at-pause pattern ${PENDING_PHRASE_PATTERN}, with or without a "RATIFIED" beside it; plus the entrance while its own status field reads draft`}
      />
      {queue.map((ms) => (
        <QueueCard
          key={ms.surface.id}
          item={ms}
          entranceStatus={
            entranceInQueue && ms.surface.ref === entranceSource
              ? entranceStatus
              : null
          }
          entranceSource={entranceSource}
        />
      ))}
      {queue.length === 0 ? (
        <p className="mb-3 text-xs text-plane-muted" data-testid="glass-stewardship-empty">
          no entry carries a pending phrase, and the entrance status does not
          read draft.
        </p>
      ) : null}
    </>
  );
}

/** One queue entry — id, stamp as carried, the matched phrase verbatim, and
 *  the full warrant / presence_exempt text quoted. Where the entry's ref is
 *  entrance.yaml's packaged sourcePath, the same card also carries that
 *  file's status field value. */
function QueueCard({
  item,
  entranceStatus,
  entranceSource,
}: {
  item: MarkedSurface;
  entranceStatus: string | null;
  entranceSource: string;
}) {
  const { surface: s, mark } = item;
  return (
    <div
      className="mb-3 rounded-md border border-border bg-card p-4 text-xs"
      data-testid={`glass-stewardship-queue-${s.id}`}
    >
      <div className="mb-1 font-medium">
        <code className="text-sm">{s.id}</code>
        <span className="ml-3 text-plane-muted">
          kind: {s.kind} · stamp: {s.stamp.toUpperCase()}
        </span>
      </div>
      <p className="mb-1">
        <span className="text-plane-muted">ref: </span>
        <code>{s.ref}</code>
      </p>
      <p className="mb-2">
        <span className="text-plane-muted">phrase found: </span>
        {mark.pendingPhrase !== null ? (
          <>
            "{mark.pendingPhrase}"
            {mark.hasRatified ? (
              <span className="text-plane-muted">
                {" "}
                — beside a "RATIFIED" in the same text
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-plane-muted">
            none — the text matches no ratify-at-pause phrase
          </span>
        )}
      </p>
      {entranceStatus !== null ? (
        <p className="mb-2" data-testid="glass-stewardship-entrance">
          <span className="text-plane-muted">status field in </span>
          <code>{entranceSource}</code>
          <span className="text-plane-muted"> reads: </span>
          <code>{entranceStatus}</code>
        </p>
      ) : null}
      {s.warrant !== undefined ? (
        <QuotedField label="warrant" text={s.warrant} />
      ) : null}
      {s.presence_exempt !== undefined ? (
        <QuotedField label="presence_exempt" text={s.presence_exempt} />
      ) : null}
    </div>
  );
}

/** Every surface entry in its measured mark group — counts measured at
 *  render, labels naming the measurement. */
function AllSurfacesSection({
  groups,
}: {
  groups: ReadonlyMap<string, MarkedSurface[]>;
}) {
  return (
    <>
      <SectionHeading
        label="All surfaces"
        title="Every entry by its measured ratification mark"
        note="phrase presence in warrant + presence_exempt text — the generator's declared derivation, regrouped here"
      />
      {MARK_GROUP_ORDER.map((g) => {
        const list = groups.get(g) ?? [];
        if (list.length === 0) return null;
        return (
          <div
            key={g}
            className="mb-3 rounded-md border border-border bg-card p-4 text-xs"
          >
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
              {g} ({list.length}) — {MARK_GROUP_MEASURE[g]}
            </div>
            <ul className="space-y-0.5">
              {list.map(({ surface: s, mark }) => (
                <li key={s.id}>
                  <code>{s.id}</code>{" "}
                  <span className="text-plane-muted">
                    ({s.kind} · {s.stamp.toUpperCase()}
                    {surfaceDates(s, mark)})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

/** Dates the entry itself carries — verified, or the phrase — displayed,
 *  never aged. */
function surfaceDates(
  s: GlassActivenessSurface,
  mark: GlassRatificationMark,
): string {
  const parts: string[] = [];
  if (s.verified !== undefined) parts.push(`verified ${s.verified}`);
  if (mark.pendingPhrase !== null) parts.push(`"${mark.pendingPhrase}"`);
  return parts.length > 0 ? ` · ${parts.join(" · ")}` : "";
}

/** Covered vs findable — every route's ref joined to the register by exact
 *  path: the row's state as the register carries it, or the measured miss. */
function CoveredFindableSection({
  lines,
}: {
  lines: readonly { id: string; ref: string; row: GlassRegisterRow | null }[];
}) {
  return (
    <>
      <SectionHeading
        label="Covered vs findable"
        title={`The ${lines.length} routed entries, joined to the register`}
        note="each routed entry's ref is matched against the packaged register rows by exact path equality; where a row is found, its state renders as the register carries it"
      />
      <div className="mb-3 rounded-md border border-border bg-card p-4 text-xs">
        <ul className="space-y-0.5">
          {lines.map(({ id, ref, row }) => (
            <li key={id}>
              <code>{id}</code> — <code>{ref}</code> —{" "}
              {row !== null ? (
                <span>
                  {row.state.toUpperCase()} ({row.verdict}; checked{" "}
                  {row.checked_on})
                </span>
              ) : (
                <span className="text-plane-muted">
                  no register row at this exact path in the packaged register
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/** The new-document path — a pointer to the tool, not a claim about it. */
function NewDocumentSection() {
  return (
    <>
      <SectionHeading
        label="The new-document path"
        title="One act: the checker-row template and the proposed route"
        note="the tool's path"
      />
      <div className="mb-3 rounded-md border border-border bg-card p-4 text-xs">
        <code>{NEW_DOCUMENT_TOOL}</code>
      </div>
    </>
  );
}

function SectionHeading({
  label,
  title,
  note,
}: {
  label: string;
  title: string;
  note: string;
}) {
  return (
    <div className="mb-3 mt-8 first:mt-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="text-xs text-plane-muted">{note}</p>
    </div>
  );
}

function QuotedField({ label, text }: { label: string; text: string }) {
  return (
    <p className="mb-1 whitespace-pre-wrap">
      <span className="text-plane-muted">{label} reads: </span>
      {text}
    </p>
  );
}
